import { IUploadImage } from '@Domains/UploadImage';
import {
  ICompletedSection,
  IIntroduction,
  IOtherDescription,
  IRemark,
} from '@Domains/utils';
import useAxios from 'axios-hooks';
import {
  CommitteeType,
  GoalType,
  SubcommitteeType,
  VerifyType,
} from 'src/Enum/EsgGreenhouse';

export type CommitteeOptionsType =
  | 'boardResponsibility'
  | 'subcommitteeResponsibility';

export const committeeOptions: {
  value: CommitteeOptionsType;
  label: string;
}[] = [
  {
    value: 'boardResponsibility',
    label: 'คณะกรรมการบริษัท',
  },
  {
    value: 'subcommitteeResponsibility',
    label: 'คณะกรรมการชุดย่อย',
  },
];
export interface IGreenhouse {
  id?: number;
  greenhouseStandard?: IGreenhouseStandard;
  greenhouseGoalTerm?: IGreenhouseGoalTerm;
  greenhouseStrategy?: IGreenhouseStrategy;
  climateChangeStructure?: IClimateChangeStructure;
  performanceGhGasMgmtReport?: IPerformanceGhGasMgmtReport;
  otherDescriptions?: Array<IOtherDescription> | null;
  completedSection?: ICompletedSection;
}

export interface IGreenhouseStandard {
  remark?: IRemark;
  principles: Array<IGreenhouseMasterData>;
  completedSection?: ICompletedSection;
}

export interface IGreenhouseGoalTerm {
  introduction?: IIntroduction;
  remark?: IRemark;
  greenhouseGoal?: IGreenhouseGoal;
  netZero?: INetZero;
  carbon?: ICarbon;
  other?: IGreenhouseGasOther;
  completedSection?: ICompletedSection;
}

export interface IGreenhouseGoal {
  id?: number;
  remark?: IRemark;
  goals: Array<GoalType>;
}
export interface INetZero {
  id?: number;
  remark?: IRemark;
  details: Array<INetZeroDetail>;
}

export interface INetZeroDetail {
  id?: number;
  scopeId: number;
  baseYear: ITermTarget;
  nearTermTarget?: ITermTarget;
  longTermTarget?: ITermTarget;
  isTgo: boolean;
  tgoId?: number | null;
  isSbti: boolean;
  sbtiId?: number | null;
}

export interface ICarbon {
  id?: number;
  remark?: IRemark;
  details: Array<ICarbonDetail>;
}

export interface ICarbonDetail {
  id?: number;
  scopeId: number;
  baseYear?: ITermTarget;
  target?: ITermTarget;
  isGuarantee: boolean;
  organizationId?: number | null;
  organizationTh?: string | null;
  organizationEn?: string | null;
}

export interface IGreenhouseGasOther {
  id?: number;
  remark?: IRemark | null;
  details: Array<IGreenhouseGasOtherDetail>;
}

export interface IGreenhouseGasOtherDetail {
  id?: number;
  scopeId: number;
  baseYear?: ITermTarget;
  nearTermTarget?: ITermTarget;
  longTermTarget?: ITermTarget;
}

export interface ITermTarget {
  year?: Date | string | null;
  gasEmissionPercent?: number | null;
  gasEmissionQuantity?: number | null;
}

export interface IGreenhouseStrategy {
  introduction?: IIntroduction;
  remark?: IRemark;
  plan: IGreenhousePlan;
  completedSection?: ICompletedSection;
}

export interface IGreenhousePlan {
  remark?: IRemark;
  descriptionTh?: string | null;
  descriptionEn?: string | null;
  urlTh?: string | null;
  urlEn?: string | null;
  pageNumberTh?: string | null;
  pageNumberEn?: string | null;
}

export interface IClimateChangeStructure {
  introduction?: IIntroduction;
  remark?: IRemark;
  committee: Array<CommitteeType>;
  boardResponsibility?: IBoardResponsibility;
  subcommitteeResponsibility?: ISubcommitteeResponsibility;
  completedSection?: ICompletedSection;
}

export interface IBoardResponsibility {
  remark?: IRemark;
  descriptionTh: string | null;
  descriptionEn: string | null;
  policyUrlTh?: string | null;
  policyUrlEn?: string | null;
  policyPageTh?: string | null;
  policyPageEn?: string | null;
}

export interface ISubcommitteeResponsibility {
  id?: number;
  remark?: IRemark;
  subcommittees: Array<ISubcommittee>;
}

export interface ISubcommittee {
  id?: number;
  subcommitteeType: SubcommitteeType;
  nameTh?: string;
  nameEn?: string;
  subcommitteeRoles?: Array<IGreenhouseMasterData>;
  otherSubcommitteeRoles?: Array<IGreenhouseMasterData>;
  responsibilitiesTh?: string | null;
  responsibilitiesEn?: string | null;
  charterUrlTh?: string | null;
  charterUrlEn?: string | null;
  charterPageTh?: string | null;
  charterPageEn?: string | null;
}

export interface IPerformanceGhGasMgmtReport {
  introduction?: IIntroduction;
  remark?: IRemark;
  performanceGhGasMgmt: IPerformanceGhGasMgmt;
  ghGasEmissionVolume?: IGhGasEmissionVolume;
  ghGasEmissionVerify?: IGhGasEmissionVerify;
  completedSection?: ICompletedSection;
}

export interface IPerformanceGhGasMgmt {
  id?: number;
  remark?: IRemark;
  result: VerifyType;
  descriptionTh?: string | null;
  descriptionEn?: string | null;
  assets?: Array<IUploadImage> | null;
}

export interface IGhGasEmissionVolume {
  totalGhGasConsumption?: IGhGasEmissionValue;
  remarkTotalGhGas?: IRemark;
  remarkScope1?: IRemark;
  remarkScope2?: IRemark;
  remarkScope3?: IRemark;
  ghGasScope1?: IGhGasEmissionValue;
  ghGasScope2?: IGhGasEmissionValue;
  ghGasScope3?: IGhGasEmissionValue;
}

export interface IGhGasEmissionValue {
  id?: number;
  asOfYear: number | null;
  asOfYesteryear: number | null;
  asOfYearBeforeYesteryear: number | null;
}

export interface IGhGasEmissionVerify {
  id?: number;
  remark?: IRemark;
  verify: VerifyType;
  verifier?: IGreenhouseMasterData;
}

export interface IGreenhouseMasterData {
  id?: number;
  infoCategoryMasterDataId: number;
  nameTh?: string;
  nameEn?: string;
}

export const useSaveEsgGreenhouse = (
  reportId?: string,
  payload?: IGreenhouse
) => {
  return useAxios<IGreenhouse>(
    {
      url: `/esg-plans/greenhouse/${reportId}`,
      data: payload,
      method: 'POST',
    },
    { manual: true }
  );
};

export const useGetEsgGreenhouse = (reportId?: string) => {
  return useAxios<IGreenhouse>({
    url: `/esg-plans/greenhouse/${reportId}`,
  });
};

export const useGetEsgGreenhouseOneReport = (
  reportId: string,
  companyScpId: number,
  asOfYear: number
) => {
  return useAxios(
    {
      url: `/esg-plans/greenhouse/${reportId}/esg-report`,
      method: 'POST',
      data: { companyScpId, asOfYear },
    },
    { manual: true }
  );
};
